import { useState, memo } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Users, ChevronDown, ChevronUp, History, ArrowRightLeft, UserMinus } from 'lucide-react'
import type { SpecialistWithAssignments, AssignedCaregiver } from '@/types/specialist-assignment'
import { TransferCaregiverDialog } from './TransferCaregiverDialog'
import { UnassignCaregiverDialog } from './UnassignCaregiverDialog'
import { AssignmentHistoryModal } from './AssignmentHistoryModal'
import { format } from 'date-fns'

interface SpecialistAssignmentCardProps {
  specialist: SpecialistWithAssignments
  allSpecialists: SpecialistWithAssignments[]
  onUpdate: () => void
}

function SpecialistAssignmentCardComponent({
  specialist, 
  allSpecialists,
  onUpdate 
}: SpecialistAssignmentCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false)
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [selectedCaregiver, setSelectedCaregiver] = useState<AssignedCaregiver | null>(null)

  const activeCaregivers = specialist.caregivers.filter(cg => cg.status === 'active')

  const handleTransfer = (caregiver: AssignedCaregiver) => {
    setSelectedCaregiver(caregiver)
    setTransferDialogOpen(true)
  }

  const handleUnassign = (caregiver: AssignedCaregiver) => {
    setSelectedCaregiver(caregiver)
    setUnassignDialogOpen(true)
  }

  const handleViewHistory = (caregiver: AssignedCaregiver) => {
    setSelectedCaregiver(caregiver)
    setHistoryModalOpen(true)
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                {specialist.specialist_name}
                {!specialist.specialist_active && (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </CardTitle>
              <CardDescription>{specialist.specialist_email}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1">
                <Users className="h-3 w-3" />
                {specialist.caregiver_count} {specialist.caregiver_count === 1 ? 'Caregiver' : 'Caregivers'}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        {expanded && activeCaregivers.length > 0 && (
          <CardContent>
            <div className="space-y-2">
              {activeCaregivers.map(caregiver => (
                <div
                  key={caregiver.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{caregiver.name}</div>
                    <div className="text-sm text-muted-foreground truncate">
                      {caregiver.email}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {caregiver.children_count} {caregiver.children_count === 1 ? 'Child' : 'Children'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Assigned {format(new Date(caregiver.assigned_at), 'MMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewHistory(caregiver)}
                      title="View history"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTransfer(caregiver)}
                      title="Transfer to another specialist"
                    >
                      <ArrowRightLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUnassign(caregiver)}
                      title="Unassign from specialist"
                    >
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {selectedCaregiver && (
        <>
          <TransferCaregiverDialog
            open={transferDialogOpen}
            onOpenChange={setTransferDialogOpen}
            caregiver={selectedCaregiver}
            currentSpecialist={specialist}
            allSpecialists={allSpecialists.filter(s => 
              s.specialist_id !== specialist.specialist_id && s.specialist_active
            )}
            onSuccess={onUpdate}
          />
          <UnassignCaregiverDialog
            open={unassignDialogOpen}
            onOpenChange={setUnassignDialogOpen}
            caregiver={selectedCaregiver}
            specialist={specialist}
            onSuccess={onUpdate}
          />
          <AssignmentHistoryModal
            open={historyModalOpen}
            onOpenChange={setHistoryModalOpen}
            caregiverId={selectedCaregiver.id}
            caregiverName={selectedCaregiver.name}
          />
        </>
      )}
    </>
  )
}

// Memoize the component to prevent unnecessary re-renders
export const SpecialistAssignmentCard = memo(SpecialistAssignmentCardComponent)
